import React, { useState, useRef, useEffect, useCallback } from 'react';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { toast } from '../../common/toast';
import axiosInstance from '../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../store.js';
import { useUser } from '../UserContext.js';
import logger from '../../utils/logger';

const PDF_ONLY_ERROR = 'Only PDF stipulations can be accepted';
const MAX_FILE_BYTES = 25 * 1024 * 1024;

function isPdfFile(file) {
  if (!file) return false;
  const name = file.name?.toLowerCase() ?? '';
  return file.type === 'application/pdf' || name.endsWith('.pdf');
}

function StipUploadDialog({ open, onClose }) {
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const { user } = useUser();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setIsDragOver(false);
    setValidationError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const validateFile = (file) => {
    if (!isPdfFile(file)) {
      return PDF_ONLY_ERROR;
    }
    if (file.size > MAX_FILE_BYTES) {
      return 'File size must be less than 25MB';
    }
    return null;
  };

  const uploadFile = useCallback(
    async (file) => {
      setValidationError('');
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        return;
      }

      const formData = new FormData();
      formData.append('file', file, file.name);
      if (negotiationObj?.muniString) {
        formData.append('muniString', negotiationObj.muniString);
      }
      if (negotiationObj?.TaxYear) {
        formData.append('taxYear', String(negotiationObj.TaxYear));
      }
      const courtDate =
        negotiationObj?.CourtDate && negotiationObj.CourtDate !== 'All'
          ? negotiationObj.CourtDate
          : '';
      if (courtDate) {
        formData.append('courtDate', courtDate);
      }
      if (user?.userName) {
        formData.append('userName', user.userName);
      }
      if (user?.email) {
        formData.append('userEmail', user.email);
      }

      setUploading(true);
      try {
        await axiosInstance.post('/upload-signed-stip-email', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Signed stipulations sent successfully.');
        resetState();
        onClose();
      } catch (err) {
        logger.error('[TownView] Signed stipulations upload failed:', err);
        const msg =
          err?.response?.data?.error || 'Failed to send signed stipulations. Please try again.';
        setValidationError(msg);
      } finally {
        setUploading(false);
      }
    },
    [negotiationObj, onClose, resetState, user],
  );

  const handleFileSelect = (file) => {
    if (uploading) return;
    uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!uploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClickUpload = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose();
  };

  return (
    <StableMuiDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Signed Stipulations</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a signed stipulation document (PDF only). It will be emailed to Aventine for
          processing.
        </Typography>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <Box
          sx={{
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
            cursor: uploading ? 'default' : 'pointer',
            opacity: uploading ? 0.7 : 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': uploading
              ? {}
              : {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          {uploading ? (
            <CircularProgress size={48} sx={{ mb: 2 }} />
          ) : (
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: isDragOver ? 'primary.main' : 'grey.400',
                mb: 2,
              }}
            />
          )}
          <Typography variant="h6" sx={{ mb: 1 }}>
            {uploading
              ? 'Sending stipulations...'
              : isDragOver
                ? 'Drop file here'
                : 'Drag and drop PDF here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uploading ? 'Please wait' : 'or click to upload'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            PDF only (max 25MB)
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
      </DialogActions>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </StableMuiDialog>
  );
}

export default StipUploadDialog;
